import React, { useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import Header from '../../components/Header';
import UpiForm from './components/UpiForm';
import BankForm from './components/BankForm';

import { useAddUpi, useAddBankAccount } from '../../api/useAddWithdrawalMode';
import { useConfigStore } from '../../storage/configState';
import { useUserStore } from '../../storage/userState';
import { useTokenStore } from '../../storage/tokenState';
import { fetchAndStoreUserDetails } from '../../services/AuthService';
import type { ELanguage } from '../../storage/store';
import {
  setModeJustAdded,
  setSelectedWithdrawMode,
} from '../../storage/withdrawState';
import { NAVIGATION_ROUTES, CLEVERTAP_EVENTS } from '../../constant';
import { pushClevertapEvent } from '../../utils/analytics/clevertap';
import type { AddModeAction, WithdrawMode } from '../../interfaces/Withdrawal';
import type { IBankFormValues } from './helper';
import styles from './styles.module.css';

type TStep = 'PICK' | 'UPI' | 'BANK';

/**
 * Adds a withdrawal mode — UPI ID or bank account.
 *
 * Mirrors Android's `UpiBottomSheet` and `AddBankAccountFragment`, including
 * their config-driven validation. On success it flags the withdrawal screen to
 * re-open its session, so the server's mode data reflects the new mode.
 */
const WithdrawAddMode: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { postSignupConfig } = useConfigStore();
  const { language } = useTokenStore();
  const { userDetails } = useUserStore();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Which modes the server will actually accept, handed over by /withdraw.
  // Falls back to both when opened directly, so the screen still works
  // standalone.
  const offered = ((location.state as { offeredModes?: WithdrawMode[] })
    ?.offeredModes ?? ['UPI', 'BANK']) as WithdrawMode[];
  const showUpi = offered.includes('UPI');
  const showBank = offered.includes('BANK');

  // With a single accepted mode the picker is a pointless extra tap — and
  // worse, offering the other one lets the user link something the server will
  // not withdraw to.
  const initialStep: TStep =
    showUpi && showBank ? 'PICK' : showBank ? 'BANK' : showUpi ? 'UPI' : 'PICK';
  const [step, setStep] = useState<TStep>(initialStep);

  const { addUpi, isLoading: isAddingUpi } = useAddUpi();
  const { addBankAccount, isLoading: isAddingBank } = useAddBankAccount();

  const goBackToWithdraw = () => navigate(NAVIGATION_ROUTES.WITHDRAW);

  /**
   * The backend rejects anything outside ADD_BANK / ADD_VPA / EDIT_BANK /
   * EDIT_VPA, and picking the wrong half is a 417.
   *
   * The caller knows best — /withdraw/modes has just fetched the linked state —
   * so an explicit `isEdit` wins. Falling back to the user store covers direct
   * entry, and correctly yields EDIT for a user replacing a mode they already
   * have rather than adding a second one.
   */
  const isEditFromState = (location.state as { isEdit?: boolean })?.isEdit;

  const resolveAction = (mode: WithdrawMode): AddModeAction => {
    const alreadyLinked =
      mode === 'UPI'
        ? Boolean(userDetails?.upiId)
        : Boolean(userDetails?.bankDetails);
    const isEdit = isEditFromState ?? alreadyLinked;

    if (mode === 'UPI') return isEdit ? 'EDIT_VPA' : 'ADD_VPA';
    return isEdit ? 'EDIT_BANK' : 'ADD_BANK';
  };

  const handleSuccess = async (mode: WithdrawMode) => {
    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_WITHDRAW_ADD_MODE_SUCCESS, {
      mode,
    });

    // Await the refresh BEFORE navigating. /withdraw re-opens its session on
    // `modeJustAdded`, and that request derives isBankAvailable/isUpiAvailable
    // from userDetails — refreshing afterwards would re-send the pre-link
    // values and leave the user on a screen that still has no usable mode.
    setIsRefreshing(true);
    try {
      await fetchAndStoreUserDetails(language as ELanguage);
    } catch {
      // Non-fatal: /withdraw re-opens its session anyway, and the mode row will
      // catch up on the next refresh rather than blocking the success path.
    } finally {
      setIsRefreshing(false);
    }

    setModeJustAdded(true);
    setSelectedWithdrawMode(mode);
    toast.success(
      mode === 'UPI' ? t('upiLinkedSuccess') : t('bankLinkedSuccess'),
    );
    goBackToWithdraw();
  };

  const handleFailure = (mode: WithdrawMode, error: unknown) => {
    const message = (error as Error)?.message || t('errorOccurred');
    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_WITHDRAW_ADD_MODE_FAILED, {
      mode,
      reason: message,
    });
    toast.error(message);
  };

  const handleAddUpi = (upiId: string) => {
    addUpi(
      { upiId: upiId.trim(), action: resolveAction('UPI') },
      {
        onSuccess: () => handleSuccess('UPI'),
        onError: (error) => handleFailure('UPI', error),
      },
    );
  };

  const handleAddBank = (values: IBankFormValues) => {
    addBankAccount(
      {
        accountHolderName: values.accountHolderName.trim(),
        accountNumber: values.accountNumber.trim(),
        ifscCode: values.ifscCode.trim().toUpperCase(),
        action: resolveAction('BANK'),
      },
      {
        onSuccess: () => handleSuccess('BANK'),
        onError: (error) => handleFailure('BANK', error),
      },
    );
  };

  const startStep = (next: TStep) => {
    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_WITHDRAW_ADD_MODE_START, {
      mode: next,
    });
    setStep(next);
  };

  const titleKey =
    step === 'UPI' ? 'addUpiTitle' : step === 'BANK' ? 'addBankTitle' : 'addModeTitle';

  return (
    <div className={styles.addModePage}>
      <Header
        pageTitle={t(titleKey)}
        navigate={
          step === 'PICK' || initialStep !== 'PICK'
            ? goBackToWithdraw
            : () => setStep('PICK')
        }
      />

      <div className={styles.addModeBody}>
        {step === 'PICK' && (
          <>
            <p className={clsx(styles.pickIntro, 'fontSizeSm14 fontColor666')}>
              {t('addModeIntro')}
            </p>
            {showUpi && <button
              type="button"
              className={styles.pickOption}
              onClick={() => startStep('UPI')}
            >
              <span className="fontSizeSm14 fontWeightBold">
                {t('addUpiTitle')}
              </span>
              <span className="fontSizeSm12 fontColor666">
                {t('addUpiSubtitle')}
              </span>
            </button>}
            {showBank && <button
              type="button"
              className={styles.pickOption}
              onClick={() => startStep('BANK')}
            >
              <span className="fontSizeSm14 fontWeightBold">
                {t('addBankTitle')}
              </span>
              <span className="fontSizeSm12 fontColor666">
                {t('addBankSubtitle')}
              </span>
            </button>}
          </>
        )}

        {step === 'UPI' && (
          <UpiForm
            upiRegex={postSignupConfig?.upiRegex}
            isSubmitting={isAddingUpi || isRefreshing}
            onSubmit={handleAddUpi}
          />
        )}

        {step === 'BANK' && (
          <BankForm
            bankRegex={postSignupConfig?.bankRegex}
            ifscRegex={postSignupConfig?.ifscRegex}
            isSubmitting={isAddingBank || isRefreshing}
            onSubmit={handleAddBank}
          />
        )}
      </div>
    </div>
  );
};

export default WithdrawAddMode;
